/**
 * Mordeczka.eu Admin - Languages Page with Ant Design
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Space,
  Typography,
  Popconfirm,
  message,
  Card,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { languagesApi } from "../services/api";
import type { Language } from "../types";
import { usePermissions } from "../hooks/usePermissions";

const { Title } = Typography;

export function LanguagesPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Language | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 100 });
  const { canEdit, canDelete } = usePermissions();

  const { data, isLoading } = useQuery({
    queryKey: ["languages", pagination],
    queryFn: () =>
      languagesApi.list({
        page: pagination.current,
        page_size: pagination.pageSize,
      }),
  });

  const createMutation = useMutation({
    mutationFn: languagesApi.create,
    onSuccess: () => {
      message.success("Język utworzono pomyślnie");
      queryClient.invalidateQueries({ queryKey: ["languages"] });
      closeModal();
    },
    onError: () => message.error("Nie udało się utworzyć"),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: {
        name?: string;
        code?: string;
        display_order?: number;
        flag_url?: string | null;
      };
    }) => languagesApi.update(id, data),
    onSuccess: () => {
      message.success("Język zaktualizowano pomyślnie");
      queryClient.invalidateQueries({ queryKey: ["languages"] });
      closeModal();
    },
    onError: () => message.error("Nie udało się zaktualizować"),
  });

  const deleteMutation = useMutation({
    mutationFn: languagesApi.delete,
    onSuccess: () => {
      message.success("Język usunięto pomyślnie");
      queryClient.invalidateQueries({ queryKey: ["languages"] });
    },
    onError: () => message.error("Nie udało się usunąć"),
  });

  const openModal = (item?: Language) => {
    setEditingItem(item || null);
    form.setFieldsValue({
      name: item?.name || "",
      code: item?.code || "",
      display_order:
        item?.display_order !== undefined ? item.display_order : 100,
      flag_url: item?.flag_url || "",
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingItem) {
        updateMutation.mutate({ id: editingItem.id, data: values });
      } else {
        createMutation.mutate(values);
      }
    } catch {
      // Validation failed
    }
  };

  const columns: ColumnsType<Language> = [
    {
      title: "Kod",
      dataIndex: "code",
      key: "code",
      width: 100,
    },
    {
      title: "Flaga",
      dataIndex: "flag_url",
      key: "flag",
      width: 80,
      render: (url: string) =>
        url ? (
          <img
            src={url}
            alt="flag"
            style={{ width: 24, boxShadow: "0 0 2px rgba(0,0,0,0.2)" }}
          />
        ) : null,
    },
    {
      title: "Kolejność",
      dataIndex: "display_order",
      key: "display_order",
      width: 100,
      sorter: (a: Language, b: Language) =>
        (a.display_order || 0) - (b.display_order || 0),
      defaultSortOrder: "ascend",
    },
    {
      title: "Nazwa",
      dataIndex: "name",
      key: "name",
      render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
      sorter: (a: Language, b: Language) => a.name.localeCompare(b.name),
    },
    {
      title: "Akcje",
      key: "actions",
      width: 120,
      render: (_: unknown, record: Language) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openModal(record)}
          />
          <Popconfirm
            title="Usunąć ten język?"
            description="Tej operacji nie można cofnąć."
            onConfirm={() => deleteMutation.mutate(record.id)}
            okText="Usuń"
            cancelText="Anuluj"
            okButtonProps={{ danger: true }}
            disabled={!canDelete}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              disabled={!canDelete}
              title={!canDelete ? "Brak uprawnień do usuwania" : "Usuń"}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          Języki
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openModal()}
        >
          Dodaj nowy
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={data?.items || []}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data?.total || 0,
            showSizeChanger: true,
            pageSizeOptions: ["100", "500", "1000"],
            showTotal: (total: number) => `Łącznie ${total} języków`,
            onChange: (page: number, pageSize: number) =>
              setPagination({ current: page, pageSize }),
          }}
        />
      </Card>

      <Modal
        title={editingItem ? "Edytuj język" : "Dodaj język"}
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={closeModal}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        okButtonProps={{
          disabled: !canEdit,
          title: !canEdit ? "Brak uprawnień do edycji" : undefined,
        }}
        okText="Zapisz"
        cancelText="Anuluj"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="code"
            label="Kod"
            rules={[
              {
                required: true,
                message: "Proszę wprowadzić kod języka (np. en, pl)",
              },
            ]}
          >
            <Input placeholder="np. en, pl, de" maxLength={5} />
          </Form.Item>
          <Form.Item
            name="name"
            label="Nazwa"
            rules={[{ required: true, message: "Proszę wprowadzić nazwę" }]}
          >
            <Input placeholder="np. Angielski, Polski, Niemiecki" />
          </Form.Item>
          <Form.Item
            name="display_order"
            label="Kolejność"
            rules={[{ required: true, message: "Proszę wprowadzić kolejność" }]}
            initialValue={100}
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="flag_url" label="Link do flagi">
            <Input placeholder="Link do obrazka flagi (np. z flagcdn.com)" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default LanguagesPage;
